import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import {
  BackButton,
  classNames,
  EnglishKeyboardLayout,
  GermanKeyboardLayout,
  Keyboard,
  layout,
  PushButton,
  TextInput,
  typography,
  useBarcodeListener,
  useTypeRef,
  View,
  ViewContent,
  ViewDescription,
  ViewFooter,
  ViewFooterItem,
  ViewHeader,
  ViewHeaderItem,
  ViewTitle
} from "@variocube/cube-ui";
import {commonStyles} from "./commonStyles";
import {style, stylesheet} from "typestyle";
import {Barcode, Language} from "@variocube/app-api";
import {useLocalization} from "../i18n";

interface TrackingNumberInputViewProps{
  onBack: () => void;
  onSelect: (trackingNumber: string) => void;
  /* Tracking Number is mandatory or not */
  required: boolean;
}

export function TrackingNumberInputView({onBack, onSelect, required}: TrackingNumberInputViewProps){
  const {t, language} = useLocalization();

  const [value, setValue] = useState("");
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [hideKeyboardTimeout, setHideKeyboardTimeout] = useState<number>();
  const [error, setError] = useState(false);
  const [type, setType] = useTypeRef();

  const handleChange = useCallback( (value: string) => {
    setValue(value);
  }, [value]);

  const handleFocus = useCallback( (e: React.FocusEvent<HTMLInputElement>) => {
    clearTimeout(hideKeyboardTimeout);
    setKeyboardOpen(true);
  }, [hideKeyboardTimeout]);

  const handleBlur = useCallback( (e: React.FocusEvent<HTMLInputElement>) => {
    setHideKeyboardTimeout(window.setTimeout( () => setKeyboardOpen(false), 250));
  }, []);

  const handleClick = useCallback( () => {
    if(required && value == ""){
      setError(true);
    }else{
      setError(false);
      onSelect(value);
    }
  }, [setError, value]);

  const handleBarcode = useCallback((barcode: Barcode) => {
    const barCode = atob(barcode.code);
    setValue(barCode);
  }, [setValue]);
  useBarcodeListener(handleBarcode);

  const feedback = useMemo( () =>  {
    if(error){
      return {
        message: t("TrackingNumberMustBeFilled"),
        "class": typography.colorError,
      }
    }
    return {
      message: t("InputTrackingNumberDescription"),
      "class": typography.muted,
    };
  }, [error])

  return(
    <View>
      <ViewHeader className={commonStyles.viewHeader}>

        <ViewHeaderItem>
          <BackButton onClick={onBack} label={t("Back")} />
        </ViewHeaderItem>

        <ViewHeaderItem fill>
          <ViewTitle>{t("InputTrackingNumberTitle")}</ViewTitle>
          <ViewDescription>{t("InputTrackingNumberDescription")}</ViewDescription>
        </ViewHeaderItem>
      </ViewHeader>

      <ViewContent
        className={classNames(layout.horizontal,layout.spaceEvenly, commonStyles.selectionViewContent)}
      >
        <div
          className={classNames(layout.vertical, layout.center, typography.alignCenter, trackingNumberInputWidth)}
        >
          <TextInput
            value={value}
            placeholder={t("TrackingNumber")}
            onChange={handleChange}
            typeRef={setType}
            onFocus={handleFocus}
            onBlur={handleBlur}
            type="text"
            outline
          />
          <p className={feedback.class}>{feedback.message}</p>
        </div>

        <div
          className={classNames(layout.vertical, layout.center, typography.alignCenter)}
        >
          <PushButton
            type="submit"
            primary
            onClick={handleClick}
            >
          OK
          </PushButton>
        </div>
      </ViewContent>

      <ViewFooter>
        <ViewFooterItem
          className={classNames(styles.keyboardExpander, keyboardOpen && styles.keyboardExpanderOpen)}
          fill
        >
          <Keyboard
            className={styles.keyboard}
            onType={type}
            layout={language == Language.DE ? GermanKeyboardLayout : EnglishKeyboardLayout}
          />
        </ViewFooterItem>
      </ViewFooter>
    </View>
  );
}

const trackingNumberInputWidth = style({
  width: "30em",
});

const styles = stylesheet({
  keyboard: {
    padding: "0.25em 0",
  },
  keyboardExpander: {
    display: "none",
    maxHeight: 0,
    transition: "maxHeight 0.5s",
  },
  keyboardExpanderOpen: {
    display: "block",
    maxHeight: "100%"
  }
});